
"use client";

import { useState } from "react";
import { ThumbsUp, ThumbsDown, AlertCircle, Calendar, FileText, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from "@/components/ui/dialog";

interface PropertyReview {
  readonly review_id: string;
  readonly public_id: string;
  readonly generated_title: string | null;
  readonly detailed_review: string;
  readonly pros: string[] | null;
  readonly cons: string[] | null;
  readonly move_in_date: Date | null;
  readonly move_out_date: Date | null;
  readonly currently_living_there: boolean;
  readonly rent_again: boolean;
  readonly created_at: Date;
}

interface LatestReviewProps {
  reviewData: PropertyReview | null;
}

const LatestReview = ({ reviewData }: LatestReviewProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Handle missing review data gracefully
  if (!reviewData) {
    return (
      <div className="glass-card p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Latest Review</h2>
        <div className="flex items-center justify-center py-8">
          <div className="text-center text-gray-500">
            <AlertCircle size={32} className="mx-auto mb-2 text-gray-400" />
            <p className="text-sm">No review available for this property</p>
            <p className="text-xs text-gray-400 mt-1">Reviews will appear when tenants submit verified experiences</p>
          </div>
        </div>
      </div>
    );
  }

  // Format rental period from database dates
  const formatRentalPeriod = (): string => {
    if (!reviewData.move_in_date) return "Rental period not specified";
    
    const moveInDate = reviewData.move_in_date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
    
    if (reviewData.currently_living_there) {
      return `Moved in: ${moveInDate} (Current tenant)`;
    }
    
    if (reviewData.move_out_date) {
      const moveOutDate = reviewData.move_out_date.toLocaleDateString('en-US', { 
        month: 'long', 
        year: 'numeric' 
      });
      return `Rented: ${moveInDate} - ${moveOutDate}`;
    }
    
    return `Moved in: ${moveInDate}`;
  };

  // Generate review title from database
  const getReviewTitle = (): string => {
    if (reviewData.generated_title) {
      return reviewData.generated_title;
    }
    
    // Extract first meaningful sentence from detailed review
    if (reviewData.detailed_review) {
      const sentences = reviewData.detailed_review.split('.').filter(s => s.trim().length > 10);
      if (sentences.length > 0) {
        const firstSentence = sentences[0].trim();
        return firstSentence.length > 60 ? `${firstSentence.substring(0, 60)}...` : firstSentence;
      }
    }
    
    return "Tenant Review";
  };

  // Truncate detailed review for summary display
  const getTruncatedReview = (): string => {
    if (!reviewData.detailed_review) return "No detailed review provided.";
    
    const maxLength = 300;
    if (reviewData.detailed_review.length <= maxLength) {
      return reviewData.detailed_review;
    }
    
    const truncated = reviewData.detailed_review.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    return `${truncated.substring(0, lastSpace)}...`;
  };

  return (
    <>
      <div className="glass-card p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Latest Review</h2>
        
        {/* Review Header */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{getReviewTitle()}</h3>
          <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
            <Calendar size={14} />
            <span>{formatRentalPeriod()}</span>
          </div>
        </div>
        
        {/* Review Content */}
        <div className="mb-6">
          <div className="flex items-start gap-2 mb-2">
            <FileText size={16} className="text-gray-400 mt-1 shrink-0" />
            <p className="text-gray-700 text-sm leading-relaxed">
              {getTruncatedReview()}
            </p>
          </div>
        </div>
        
        {/* Pros and Cons - Only show if data exists */}
        {(reviewData.pros && reviewData.pros.length > 0) || (reviewData.cons && reviewData.cons.length > 0) ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Pros */}
            {reviewData.pros && reviewData.pros.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Pros</h4>
                <div className="flex flex-wrap gap-2">
                  {reviewData.pros.slice(0, 4).map((pro, i) => (
                    <span key={i} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                      {pro}
                    </span>
                  ))}
                  {reviewData.pros.length > 4 && (
                    <span className="text-xs text-gray-500 px-2 py-1">
                      +{reviewData.pros.length - 4} more
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* Cons */}
            {reviewData.cons && reviewData.cons.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Cons</h4>
                <div className="flex flex-wrap gap-2">
                  {reviewData.cons.slice(0, 4).map((con, i) => (
                    <span key={i} className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">
                      {con}
                    </span>
                  ))}
                  {reviewData.cons.length > 4 && (
                    <span className="text-xs text-gray-500 px-2 py-1">
                      +{reviewData.cons.length - 4} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="mb-6 py-4 text-center text-gray-500">
            <p className="text-sm">No specific pros and cons listed in this review</p>
          </div>
        )}
        
        {/* Footer with recommendation */}
        <div className="flex items-center justify-between border-t pt-4">
          <div className="flex items-center gap-2">
            <span className="text-gray-700">Would rent again?</span>
            {reviewData.rent_again ? (
              <ThumbsUp size={18} className="text-green-600 fill-green-600" />
            ) : (
              <ThumbsDown size={18} className="text-red-600 fill-red-600" />
            )}
            <span className="text-sm text-gray-600">
              {reviewData.rent_again ? 'Yes' : 'No'}
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={() => setIsModalOpen(true)}>
            View Full Review
          </Button>
        </div>
      </div>

      {/* Full Review Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-white border border-gray-200 shadow-2xl">
          <DialogHeader className="space-y-3 bg-white border-b border-gray-100 pb-4">
            <DialogTitle className="text-2xl font-bold text-gray-900">
              {getReviewTitle()}
            </DialogTitle>
            <div className="text-sm text-gray-600 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2">
                <Calendar size={14} className="text-gray-500" />
                <span className="text-gray-700">{formatRentalPeriod()}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-700">Would rent again:</span>
                {reviewData.rent_again ? (
                  <>
                    <ThumbsUp size={16} className="text-green-600 fill-green-600" />
                    <span className="text-green-600 font-medium">Yes</span>
                  </>
                ) : (
                  <>
                    <ThumbsDown size={16} className="text-red-600 fill-red-600" />
                    <span className="text-red-600 font-medium">No</span>
                  </>
                )}
              </div>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-6 py-4 bg-white">
            {/* Full Review Content */}
            <div className="space-y-4 bg-white">
              <div className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg border border-gray-100">
                <FileText size={20} className="text-gray-500 mt-1 shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-3">Full Review</h3>
                  <div className="text-gray-800 leading-relaxed whitespace-pre-wrap text-sm">
                    {reviewData.detailed_review || "No detailed review provided."}
                  </div>
                </div>
              </div>
            </div>

            {/* Pros and Cons - Expanded View */}
            {(reviewData.pros && reviewData.pros.length > 0) || (reviewData.cons && reviewData.cons.length > 0) ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white">
                {/* All Pros */}
                {reviewData.pros && reviewData.pros.length > 0 && (
                  <div className="space-y-3 bg-white">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2 bg-white">
                      <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                      <span className="text-gray-900">Pros ({reviewData.pros.length})</span>
                    </h3>
                    <div className="space-y-2">
                      {reviewData.pros.map((pro, i) => (
                        <div key={i} className="bg-green-50 border border-green-200 rounded-lg p-3 shadow-sm">
                          <span className="text-green-800 text-sm font-medium">{pro}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* All Cons */}
                {reviewData.cons && reviewData.cons.length > 0 && (
                  <div className="space-y-3 bg-white">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2 bg-white">
                      <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                      <span className="text-gray-900">Cons ({reviewData.cons.length})</span>
                    </h3>
                    <div className="space-y-2">
                      {reviewData.cons.map((con, i) => (
                        <div key={i} className="bg-red-50 border border-red-200 rounded-lg p-3 shadow-sm">
                          <span className="text-red-800 text-sm font-medium">{con}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-sm">No specific pros and cons were listed in this review</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LatestReview;
